import { Component, Input, OnChanges } from '@angular/core'
import { MenuItem, MessageService } from 'primeng/api'

import { entregarDescarga } from '../../data/utils/entregar-descarga.util'
import { ApiF098Service } from '../../data/services/api/api-f098.service'
import { LigaComplementos } from '../../data/interfaces/f098.interface'
import { AuthService } from '@data/services/modules/auth/auth.service'

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre']

/**
 * Los reportes de Contabilidad, desde el encabezado de Cobranza.
 *
 * Cada opción lleva el permiso de su endpoint y **solo aparece si se tiene**: una opción que responde
 * 403 al pulsarla es peor que no enseñarla. Si el rol no tiene ninguna, el botón entero desaparece.
 *
 * | Opción | Permiso |
 * |---|---|
 * | Libro del mes | `wallet` · `get-all` |
 * | Complementos del mes | `invoices` · `get-all` |
 * | Clientes en mora | `interest` · `moratory` |
 * | Ligar complementos con sus pagos | `invoices` · `update` |
 */
@Component({
  selector: 'app-reportes-contables',
  templateUrl: './reportes-contables.component.html',
  styleUrls: ['./reportes-contables.component.scss'],
})
export class ReportesContablesComponent implements OnChanges {
  /** El mes del cascarón, `YYYY-MM`. */
  @Input() month = ''

  descargando: string | null = null

  // ── Clientes en mora ────────────────────────────────────────────────────────────────────
  mostrarMora = false
  corte: Date = new Date()
  readonly hoy = new Date()

  // ── Ligar complementos ──────────────────────────────────────────────────────────────────
  mostrarLiga = false
  liga: LigaComplementos | null = null
  ligaCargando = false
  ligaAplicando = false
  ligaError: string | null = null

  constructor(
    private readonly api: ApiF098Service,
    private readonly auth: AuthService,
    private readonly toast: MessageService,
  ) {}

  get mesLegible(): string {
    if (!this.month) return ''
    return `${MESES[Number(this.month.slice(5, 7)) - 1]} ${this.month.slice(0, 4)}`
  }

  /** Las opciones que ESTE rol puede usar. */
  opciones: MenuItem[] = []

  //* Se arma al cambiar el mes y no en un getter: un arreglo nuevo en cada ciclo de pintado hace que el
  //* menú se vuelva a construir sin parar.
  ngOnChanges(): void {
    this.opciones = this.armarOpciones()
  }

  private armarOpciones(): MenuItem[] {
    const puede = (m: string, p: string) => this.auth.hasModulePermission(m, p)
    const items: MenuItem[] = []
    if (puede('wallet', 'get-all'))
      items.push({ label: `Libro del mes (${this.mesLegible})`, icon: 'fa-solid fa-book', title: 'CARTERA, CENTRAL, AVANCE, DASHBOARD y CUADRE en un solo archivo', command: () => this.libro() })
    if (puede('invoices', 'get-all'))
      items.push({ label: `Complementos (${this.mesLegible})`, icon: 'fa-solid fa-file-invoice', title: 'El layout del timbrador y el control de complementos', command: () => this.complementos() })
    if (puede('interest', 'moratory')) items.push({ label: 'Clientes en mora…', icon: 'fa-solid fa-user-clock', command: () => this.abrirMora() })
    const administrar: MenuItem[] = []
    if (puede('invoices', 'update')) administrar.push({ label: 'Ligar complementos con sus pagos…', icon: 'fa-solid fa-link', command: () => this.abrirLiga() })
    if (administrar.length) items.push({ separator: true }, ...administrar)
    return items
  }

  libro(): void {
    this.descargando = 'libro'
    this.api.libro(this.month).subscribe((d) => {
      this.descargando = null
      entregarDescarga(d, `CENTRAL_CARTERA_${this.month}.xlsx`, this.toast, 'Libro del mes')
    })
  }

  complementos(): void {
    this.descargando = 'complementos'
    this.api.complementos(this.month).subscribe((d) => {
      this.descargando = null
      entregarDescarga(d, `COMPLEMENTOS_${this.month}.xlsx`, this.toast, 'Complementos')
    })
  }

  abrirMora(): void {
    this.corte = new Date()
    this.mostrarMora = true
  }

  descargarMora(): void {
    if (!this.corte) return
    const d = this.corte
    const asOf = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    this.descargando = 'mora'
    this.api.clientesEnMora(asOf).subscribe((r) => {
      this.descargando = null
      if (entregarDescarga(r, `CLIENTES_EN_MORA_${asOf}.xlsx`, this.toast, 'Clientes en mora')) this.mostrarMora = false
    })
  }

  // ── Ligar complementos: primero la vista previa, luego escribir ───────────────────────────

  abrirLiga(): void {
    this.mostrarLiga = true
    this.liga = null
    this.ligaError = null
    this.ligaCargando = true
    this.api.ligarComplementos(false).subscribe((r) => {
      this.ligaCargando = false
      if (r.error) this.ligaError = r.msg
      else this.liga = r.data
    })
  }

  aplicarLiga(): void {
    this.ligaAplicando = true
    this.ligaError = null
    this.api.ligarComplementos(true).subscribe((r) => {
      this.ligaAplicando = false
      if (r.error) {
        this.ligaError = r.msg
        return
      }
      this.mostrarLiga = false
      this.toast.add({ severity: 'success', summary: 'Complementos', detail: `${r.data?.linked ?? 0} renglones de complemento quedaron ligados a su pago` })
    })
  }
}
